
import logger from '../app/logger';
import {
    CONFERENCE_JOINED,
    CONFERENCE_LEFT,
    getCurrentConference
} from '../base/conference';
import {
    MHConferenceEvents
} from '../base/lib-meet-hour';
import { PARTICIPANT_JOINED, getLocalParticipant } from '../base/participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { NOTIFICATION_TIMEOUT, showNotification } from '../notifications';

import { DONATION_MAREQUE, FORMGUID, FORM_PUBLISHED_NOTIFICATION, RESET_CLICKANDPLEDGE } from './actionTypes';
import {
    bindDonationStream,
    bindFormGUID,
    bindGiventID,
    bindStreamOptions,
    isClickandPledge,
    resetClickandPledge
} from './actions';
import { safeConferenceSendMessage } from './conferenceUtils';


/**
 * Implements the middleware of the Click&Pledge donation feature.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case CONFERENCE_LEFT: {
        const { dispatch } = store;

        // Reset Click&Pledge state when leaving conference
        dispatch(resetClickandPledge());
        break;
    }

    case CONFERENCE_JOINED:
        _addClickandPledgeMessageListeners(action.conference, store);
        break;

    case PARTICIPANT_JOINED: {
        _syncDonationStreamToNewParticipant(store, action);
        break;
    }
    }

    return next(action);
});


/**
 * Stored handler reference for cleanup.
 */
let _clickandPledgeReceiveMessage = null;

/**
 * Registers listeners for Click&Pledge related conference events.
 *
 * @param {MHConference} conference - The conference instance on which the
 * new event listeners will be registered.
 * @param {Object} store - The redux store object.
 * @private
 * @returns {void}
 */
function _addClickandPledgeMessageListeners(conference, store) {
    const { dispatch } = store;

    const receiveMessage = (_, data) => {
        // Add validation for incoming message data
        if (!data || typeof data !== 'object') {
            logger.warn('Invalid message data received');

            return;
        }

        switch (data.type) {
        case DONATION_MAREQUE: {
            const { streamData } = data;

            // Normalize: accept both single object and array
            const donations = Array.isArray(streamData) ? streamData : streamData ? [ streamData ] : [];

            if (donations.length === 0) {
                logger.warn('DONATION_MAREQUE: Invalid streamData received');
                break;
            }

            const existingData = store.getState()['features/clickandpledge']?.donationStream || [];

            // Filter out any donations that already exist (by orderNumber)
            const newDonations = donations.filter(donation =>
                donation && donation.orderNumber
                    && !existingData.some(item => item.orderNumber === donation.orderNumber)
            );

            if (newDonations.length === 0) {
                logger.info('DONATION_MAREQUE: All donations already exist, skipping');
            } else {
                const updatedStreamData = [ ...existingData, ...newDonations ];

                dispatch(bindDonationStream(updatedStreamData));
                logger.info('DONATION_MAREQUE: Stream data updated successfully');
            }
            break;
        }

        case RESET_CLICKANDPLEDGE: {
            const { myid,
                senderId } = data;

            logger.info('reset clickandpledge received', {
                myid,
                senderId
            });
            dispatch(resetClickandPledge());
            logger.info('RESET_CLICKANDPLEDGE: State reset successfully');
            break;
        }

        case FORMGUID: {
            const { clickandPledge, formGuid, streamOptions, GiventID } = data;
            const giventToken = store.getState()['features/clickandpledge']?.giventToken;
            const formID = store.getState()['features/clickandpledge']?.formGUID;

            if (!giventToken && !formID) {
                dispatch(bindFormGUID(formGuid));
                dispatch(bindGiventID(GiventID));
                dispatch(isClickandPledge(clickandPledge));
                dispatch(bindStreamOptions(streamOptions));
            }
            break;
        }

        case FORM_PUBLISHED_NOTIFICATION: {
            const { participantName } = data;

            dispatch(showNotification({
                titleArguments: { name: participantName },
                titleKey: 'notify.clickAndPledge'
            }, NOTIFICATION_TIMEOUT));
            break;
        }

        default:
            // Don't log unknown message types here as this is specific to Click&Pledge
            break;
        }
    };

    // Register message listeners
    _clickandPledgeReceiveMessage = receiveMessage;

    conference.on(
        MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED,
        receiveMessage
    );
    conference.on(
        MHConferenceEvents.NON_PARTICIPANT_MESSAGE_RECEIVED,
        receiveMessage
    );
}

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, e.g. Clear Click&Pledge state.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, store, previousConference) => {
        if (previousConference && _clickandPledgeReceiveMessage) {
            previousConference.off(
                MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED,
                _clickandPledgeReceiveMessage
            );
            previousConference.off(
                MHConferenceEvents.NON_PARTICIPANT_MESSAGE_RECEIVED,
                _clickandPledgeReceiveMessage
            );
            _clickandPledgeReceiveMessage = null;
        }
        if (conference && conference !== previousConference) {
            _addClickandPledgeMessageListeners(conference, store);
        }
    });

/**
 * Syncs donation stream data to a new participant if they don't have it.
 *
 * @param {Object} store - The redux store.
 * @param {Object} action - The PARTICIPANT_JOINED action.
 * @private
 * @returns {void}
 */
function _syncDonationStreamToNewParticipant(store) {
    const { getState } = store;
    const state = getState();
    const localParticipantid = getLocalParticipant(state)?.id;
    const conference = getCurrentConference(state);

    if (!conference) {
        return;
    }

    const donationStreamData = state['features/clickandpledge']?.donationStream || [];
    const { streamOptions, giventID, formGUID, userId } = state['features/clickandpledge'];

    if (donationStreamData.length > 0 && userId === localParticipantid) {
        setTimeout(() => {
            safeConferenceSendMessage(conference, {
                type: DONATION_MAREQUE,
                streamData: donationStreamData
            }, 'clickandpledge-givent:middleware:syncDonationStream');
        }, 1000);
    }

    if (giventID) {
        setTimeout(() => {
            safeConferenceSendMessage(conference, {
                type: FORMGUID,
                streamOptions,
                GiventID: giventID,
                clickandPledge: true,
                formGuid: formGUID
            }, 'clickandpledge-givent:middleware:syncFormGuid');
        });
    }
}


